import React, { useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { PaymentForm } from '../components/PaymentForm';
import { PaymentAPI } from '../services/api';
import { PaymentFormData, PaymentResponse } from '../types/payment';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../utils/rolePermissions';
import { AlertService } from '../services/AlertService';
import { DocumentUploadButton } from '../components/DocumentUploadButton';
import { DocumentDialog } from '../components/DocumentDialog';

export const NewPayment: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PaymentResponse | null>(null);
  const [documentDialogRecordId, setDocumentDialogRecordId] = useState<string | null>(null);

  const canCreateSales = !user?.view_only && hasPermission(user?.role, 'canCreateSales');

  const handlePaymentSubmit = async (data: PaymentFormData, type: 'sale' | 'authorize' | 'validate' | 'sale-3ri') => {
    try {
      setLoading(true);
      setResult(null);

      let response: PaymentResponse;

      switch (type) {
        case 'sale':
          response = await PaymentAPI.createSale(data);
          break;
        case 'sale-3ri':
          response = await PaymentAPI.createSale3RI(data);
          break;
        case 'authorize':
          response = await PaymentAPI.authorize(data);
          break;
        case 'validate':
          response = await PaymentAPI.validate(data);
          break;
        default:
          throw new Error('Invalid payment type');
      }

      setResult(response);

      // Throw for declined transactions so PaymentForm shows error dialog
      if (response.response_code !== '100' && !(response.response_code === '000' && response.responsetext?.includes('Manual Approval'))) {
        const declineReason = response.responsetext || response.response || 'Unknown error';
        throw new Error(declineReason);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!canCreateSales) {
    return (
      <DashboardLayout>
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to create payments.</p>
          <p>Contact your administrator if you believe this is an error.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="new-payment">
        <h2>Process Payment</h2>
        <PaymentForm onSubmit={handlePaymentSubmit} loading={loading} />

        {result && (
          <div className="payment-result">
            <h3>Transaction Result</h3>
            <div className={`result-card ${getResultStatus(result)}`}>
              <div className="result-header">
                <div className="result-status">
                  <span className={`status-badge ${getResultStatus(result)}`}>
                    {getResultStatusText(result)}
                  </span>
                </div>
                <div className="result-code">
                  Code: {result.response_code}
                </div>
              </div>

              <div className="result-details">
                <div className="detail-item">
                  <strong>Response:</strong> {result.responsetext || result.response || 'No response text available'}
                </div>
                
                {result.transactionid && (
                  <div className="detail-item">
                    <strong>Transaction ID:</strong> {result.transactionid}
                  </div>
                )}

                {result.authcode && (
                  <div className="detail-item">
                    <strong>Authorization Code:</strong> {result.authcode}
                  </div>
                )}

                {result.avsresponse && (
                  <div className="detail-item">
                    <strong>AVS Response:</strong> {result.avsresponse}
                  </div>
                )}

                {result.cvvresponse && (
                  <div className="detail-item">
                    <strong>CVV Response:</strong> {result.cvvresponse}
                  </div>
                )}
              </div>

              {/* Additional actions for successful transactions */}
              {(result.response_code === '100' || (result.response_code === '000' && result.responsetext?.includes('Manual Approval'))) && result.pending_sale_id && (
                <div className="result-actions">
                  {result.transactionid && (
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => navigator.clipboard.writeText(result.transactionid!)}
                    >
                      Copy Transaction ID
                    </button>
                  )}
                  <DocumentUploadButton
                    recordId={result.pending_sale_id}
                    onClick={() => setDocumentDialogRecordId(result.pending_sale_id!)}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .new-payment {
          max-width: 1600px;
          margin: 0 auto;
          padding: 0 1rem;
        }

        .new-payment h2 {
          margin: 0 0 1.5rem 0;
          color: var(--text-primary);
          font-size: 1.5rem;
          font-weight: 600;
        }


        .payment-result {
          margin-top: 2rem;
        }

        .payment-result h3 {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
          font-size: 1.25rem;
          font-weight: 600;
        }

        .result-card {
          background: var(--bg-card);
          border-radius: 8px;
          padding: 1.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          border-left: 4px solid var(--border-primary);
        }

        .result-card.success {
          border-left-color: var(--success);
        }

        .result-card.pending {
          border-left-color: var(--warning);
        }

        .result-card.error {
          border-left-color: var(--error);
        }

        .result-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid var(--border-primary);
        }

        .status-badge {
          padding: 0.375rem 0.75rem;
          border-radius: 20px;
          font-size: 0.875rem;
          font-weight: 500;
          text-transform: uppercase;
        }

        .status-badge.success {
          background: var(--success-light);
          color: var(--success);
        }

        .status-badge.pending {
          background: var(--warning-light);
          color: var(--warning);
        }

        .status-badge.error {
          background: var(--error-light);
          color: var(--error);
        }

        .result-code {
          font-family: monospace;
          font-weight: 600;
          color: var(--text-secondary);
        }

        .result-details {
          display: grid;
          gap: 0.75rem;
        }

        .detail-item {
          color: var(--text-primary);
        }

        .detail-item strong {
          color: var(--text-primary);
          margin-right: 0.5rem;
        }

        .result-actions {
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid var(--border-primary);
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
          align-items: center;
        }

        .access-denied {
          text-align: center;
          padding: 4rem 2rem;
          background: var(--bg-card);
          border-radius: 8px;
        }

        .access-denied h2 {
          color: var(--text-primary);
          margin-bottom: 1rem;
        }

        .access-denied p {
          color: var(--text-secondary);
          margin-bottom: 0.5rem;
        }

        @media (max-width: 768px) {
          .new-payment {
            padding: 0 1rem;
          }

          .result-header {
            flex-direction: column;
            gap: 0.5rem;
            align-items: flex-start;
          }
        }
      `}</style>

      {/* Document Dialog */}
      {documentDialogRecordId && (
        <DocumentDialog
          recordId={documentDialogRecordId}
          isOpen={!!documentDialogRecordId}
          onClose={() => setDocumentDialogRecordId(null)}
        />
      )}
    </DashboardLayout>
  );
};

function getResultStatus(result: PaymentResponse): 'success' | 'pending' | 'error' {
  if (result.response_code === '100') return 'success';
  if (result.response_code === '000' && result.responsetext?.includes('Manual Approval')) return 'pending';
  return 'error';
}

function getResultStatusText(result: PaymentResponse): string {
  if (result.response_code === '100') return 'Approved';
  if (result.response_code === '000' && result.responsetext?.includes('Manual Approval')) return 'Pending Approval';
  return 'Declined';
}